<div class="justup-dialog pilotage-dialog">
  <vl-dialog-header-export
    [exporting]="exporting()"
    (close)="dialogRef.close()"
    (export)="exportExcel()">
    {{ title() }}
  </vl-dialog-header-export>

  <div class="justup-dialog-content flex-col gap-16">

    <!-- ─── Stats cards ────────────────────────────────────────────────── -->
    <div class="flex-row gap-8 wrap">
      <vl-radio-card mode="display" color="basic" icon="receipt_long"
                     title="Commandes ouvertes"
                     style="flex: 1 1 140px; min-width: 140px">
        <div content class="flex-col gap-2">
          <span class="headline">{{ stats().count }}</span>
          <span class="tiny on-disabled">toutes étapes</span>
        </div>
      </vl-radio-card>

      <vl-radio-card mode="display" color="valid" icon="euro"
                     title="Montant HT"
                     style="flex: 1 1 140px; min-width: 140px">
        <div content class="flex-col gap-2">
          <span class="headline">{{ formatEuro(stats().totalHT) }} €</span>
          <span class="tiny on-disabled">total à recevoir</span>
        </div>
      </vl-radio-card>

      <vl-radio-card mode="display"
                     [color]="stats().nbLate > 0 ? 'error' : 'basic'"
                     icon="schedule"
                     title="En retard"
                     style="flex: 1 1 140px; min-width: 140px">
        <div content class="flex-col gap-2">
          <span class="headline">{{ stats().nbLate }}</span>
          @if (stats().nbLate > 0) {
            <span class="tiny on-disabled">
              {{ formatEuro(stats().totalLateHT) }} € · max {{ stats().maxDaysLate }}j
            </span>
          } @else {
            <span class="tiny on-disabled">aucun retard</span>
          }
        </div>
      </vl-radio-card>

      <vl-radio-card mode="display"
                     [color]="stats().avgDaysLate > 0 ? 'warn' : 'basic'"
                     icon="hourglass_empty"
                     title="Retard moyen"
                     style="flex: 1 1 140px; min-width: 140px"
                     matTooltip="Moyenne du nb de jours de retard, sur les commandes en retard uniquement">
        <div content class="flex-col gap-2">
          <span class="headline">
            {{ stats().avgDaysLate > 0 ? (stats().avgDaysLate | number:'1.0-0':'fr-FR') + ' j' : '—' }}
          </span>
          <span class="tiny on-disabled">par commande en retard</span>
        </div>
      </vl-radio-card>

      @if (stats().topTiersByHT) {
        <vl-radio-card mode="display" color="secondary" icon="emoji_events"
                       [title]="'Top ' + tiersLabel().toLowerCase()"
                       style="flex: 1 1 140px; min-width: 140px"
                       matTooltip="Tiers avec le plus gros montant HT en cours">
          <div content class="flex-col gap-2">
            <span class="body-1 bold ellipsis">
              {{ stats().topTiersByHT!.tiersDenomination }}
            </span>
            <span class="tiny on-disabled">
              {{ formatEuro(stats().topTiersByHT!.totalHT) }} € ·
              {{ stats().topTiersByHT!.count }} cmd
            </span>
          </div>
        </vl-radio-card>
      }

      @if (stats().topTiersByLate) {
        <vl-radio-card mode="display" color="error" icon="report"
                       [title]="'Plus de retards'"
                       style="flex: 1 1 140px; min-width: 140px"
                       matTooltip="Tiers avec le plus de commandes en retard">
          <div content class="flex-col gap-2">
            <span class="body-1 bold ellipsis">
              {{ stats().topTiersByLate!.tiersDenomination }}
            </span>
            <span class="tiny on-disabled">
              {{ stats().topTiersByLate!.nbLate }} en retard · max {{ stats().topTiersByLate!.maxDaysLate }}j
            </span>
          </div>
        </vl-radio-card>
      }
    </div>

    <!-- ─── Tableau "Par étape" ─────────────────────────────────────────── -->
    <section class="card">
      <h3 class="card-title">Répartition par étape</h3>
      <table class="pilotage-table table-etape">
        <thead>
          <tr>
            <th>Étape</th>
            <th class="num">Nb</th>
            <th class="num">Montant HT</th>
            <th class="num">Retard</th>
            <th class="num">%</th>
          </tr>
        </thead>
        <tbody>
          @for (row of etatBreakdown(); track row.etat) {
            <tr>
              <td>{{ row.etatLabel }}</td>
              <td class="num">{{ row.count }}</td>
              <td class="num">{{ formatEuro(row.totalHT) }} €</td>
              <td class="num" [class.text-error]="row.nbLate > 0">{{ row.nbLate }}</td>
              <td class="num" [class.text-error]="row.pctLate >= 0.5"
                              [class.text-warning]="row.pctLate >= 0.2 && row.pctLate < 0.5">
                {{ formatPct(row.pctLate) }}
              </td>
            </tr>
          }
        </tbody>
      </table>
    </section>

    <!-- ─── Tableau "Top tiers" ─────────────────────────────────────────── -->
    <section class="card">
      <h3 class="card-title">Top {{ tiersLabel().toLowerCase() }}s par montant ouvert</h3>
      <table class="pilotage-table table-tiers">
        <thead>
          <tr>
            <th>{{ tiersLabel() }}</th>
            <th class="num">Cmd</th>
            <th class="num">Montant HT</th>
            <th class="num">Retard</th>
            <th class="num">Max</th>
            <th>Dernière</th>
          </tr>
        </thead>
        <tbody>
          @for (row of topTiers(); track row.tiersDenomination) {
            <tr>
              <td class="bold">{{ row.tiersDenomination }}</td>
              <td class="num">{{ row.count }}</td>
              <td class="num">{{ formatEuro(row.totalHT) }} €</td>
              <td class="num" [class.text-error]="row.nbLate > 0">{{ row.nbLate }}</td>
              <td class="num" [class.text-error]="row.maxDaysLate > 0">
                {{ row.maxDaysLate > 0 ? row.maxDaysLate + 'j' : '—' }}
              </td>
              <td class="tiny on-disabled">
                {{ row.lastCommandeDate ? (row.lastCommandeDate | date : 'd MMM y') : '—' }}
              </td>
            </tr>
          } @empty {
            <tr><td colspan="6" class="tiny on-disabled p-12">Aucune commande</td></tr>
          }
        </tbody>
      </table>
    </section>

    <!-- ─── Tableau détail ───────────────────────────────────────────────── -->
    <section class="card">
      <div class="flex-row align-center justify-between gap-8 mb-8 wrap">
        <h3 class="card-title">Détail des commandes ({{ filteredItems().length }})</h3>
        <input class="pilotage-search" type="search"
               placeholder="Rechercher tiers ou numéro…"
               [value]="search()"
               (input)="search.set($any($event.target).value)" />
      </div>
      <table class="pilotage-table pilotage-detail">
        <thead>
          <tr>
            <th class="sortable" (click)="toggleSort('tiers')">
              {{ tiersLabel() }} {{ sortKey() === 'tiers' ? (sortDesc() ? '↓' : '↑') : '' }}
            </th>
            <th class="sortable" (click)="toggleSort('numero')">
              Numéro {{ sortKey() === 'numero' ? (sortDesc() ? '↓' : '↑') : '' }}
            </th>
            <th class="sortable" (click)="toggleSort('etat')">
              Étape {{ sortKey() === 'etat' ? (sortDesc() ? '↓' : '↑') : '' }}
            </th>
            <th class="sortable" (click)="toggleSort('datePrevue')">
              Livraison prévue {{ sortKey() === 'datePrevue' ? (sortDesc() ? '↓' : '↑') : '' }}
            </th>
            <th class="num sortable" (click)="toggleSort('daysLate')">
              Retard {{ sortKey() === 'daysLate' ? (sortDesc() ? '↓' : '↑') : '' }}
            </th>
            <th class="num sortable" (click)="toggleSort('totalHT')">
              Montant HT {{ sortKey() === 'totalHT' ? (sortDesc() ? '↓' : '↑') : '' }}
            </th>
            <th class="num">Articles</th>
          </tr>
        </thead>
        <tbody>
          @for (c of filteredItems(); track c.id) {
            <tr class="cursor-pointer"
                [class.row-overdue]="c.daysLate > 0"
                (click)="navigateToCommande(c)">
              <td class="bold">{{ c.tiersDenomination }}</td>
              <td class="caption on-disabled">{{ c.numero }}</td>
              <td><span class="etat-pill">{{ c.etatLabel }}</span></td>
              <td class="tiny">
                {{ c.dateLivraisonPrevue ? (c.dateLivraisonPrevue | date : 'd MMM y') : '—' }}
              </td>
              <td class="num">
                @if (c.daysLate > 0) {
                  <span class="late-pill">+{{ c.daysLate }}j</span>
                } @else {
                  <span class="tiny on-disabled">—</span>
                }
              </td>
              <td class="num">
                {{ c.totalHT > 0 ? (formatEuro(c.totalHT) + ' €') : '—' }}
              </td>
              <td class="num">{{ c.nbLignes }}</td>
            </tr>
          } @empty {
            <tr><td colspan="7" class="tiny on-disabled p-12">Aucun résultat</td></tr>
          }
        </tbody>
      </table>
    </section>

  </div>
</div>
